import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from 'firebase/auth';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import {
  Activity, ArrowLeft, BookOpen, Bot, Check, CheckCircle2, ChevronLeft,
  ClipboardCheck, Cpu, FileText, Flag, GraduationCap, Layers3, LayoutDashboard,
  KeyRound, LogOut, MessageSquareText, RefreshCw, Save, Send, ShieldCheck,
  ChevronsLeft, ChevronsRight, Sparkles, Target, UserCheck, UsersRound, XCircle,
  INSIGNIA_GLYPHS, StudiumEmblem, StudiumWordmark,
} from './components/Glyphs';
import LandingPage from './components/LandingPage';
import { auth, googleProvider } from './lib/firebase';
import {
  acknowledgePrivacyPolicy,
  answerLearningChallenge, approveUser, askAssistant, blockUser, configureAi, deleteConversation,
  deleteUserAccount, getAiSettings, getConversation, getKnowledgeIndex,
  getLearningJourney, getLesson, getModelCatalog, listConversations, listUsers,
  disconnectAi, provisionUser, resetLearningProgress, saveLearningProgress, setAccessPolicy, testAiConnection,
} from './lib/api';
import type { AccessPolicy, AiConfiguration, AiConnectionTest, AssistantAnswer, ConversationSummary, CourseModule, GameStageId, LearningChallengeResult, LearningJourney, LearningMutation, Lesson, ManagedUser, ModelCatalog, ModelInfo, UserAccess, View } from './lib/types';

/** The one address that holds the administrative role. It is stated here only
    so the panel can name it; the role itself is derived on the server from the
    verified Google token and is not something the browser can assert. */
const SUPER_ADMIN_EMAIL = 'rotgolan@gmail.com';

const MarkdownLesson = lazy(() => import('./components/MarkdownLesson'));
const LearningQuest = lazy(() => import('./components/LearningQuest'));
const KnowledgeSearch = lazy(() => import('./components/KnowledgeSearch'));
const PrivacyCenter = lazy(() => import('./components/PrivacyCenter'));
type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  result?: AssistantAnswer;
};

function messageFrom(error: unknown) {
  if (error instanceof FirebaseError) return error.message.replace(/^.*?:\s*/, '') || 'אירעה שגיאה. נסה שוב.';
  if (error instanceof Error) return error.message;
  return 'אירעה שגיאה בלתי צפויה. נסה שוב.';
}

function initials(name?: string | null) {
  // Never fall back to a person's name. A learner whose Google profile has no
  // display name would otherwise be shown someone else's initials.
  const source = (name || '').trim();
  if (!source) return '·';
  return source.split(/\s+/).map((part) => part[0]).join('').slice(0, 2);
}

/** A Google profile can carry a middle name, a second family name or a suffix.
    The header wants the person as they would introduce themselves — given name
    and family name — so the first and last parts are kept and the rest dropped. */
function fullName(name?: string | null, email?: string | null) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return email || 'לומד/ת מאושר/ת';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

/** OpenRouter ids are `provider/model`, so the provider is the first segment. */
function providerOf(modelId: string) {
  return modelId.includes('/') ? modelId.slice(0, modelId.indexOf('/')) : 'other';
}

type ModelSort = 'price-asc' | 'price-desc' | 'name' | 'provider' | 'context';

function formatPrice(price: number) {
  if (price === 0) return 'חינם';
  return `$${price.toFixed(price < 1 ? 3 : 2)} / 1M`;
}

function formatDate(value: unknown) {
  if (!value || typeof value !== 'object' || !('seconds' in value)) return 'כעת';
  return new Date(Number(value.seconds) * 1000).toLocaleDateString('he-IL');
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [access, setAccess] = useState<UserAccess | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => onAuthStateChanged(auth, async (nextUser) => {
    setUser(nextUser);
    setAccess(null);
    setAuthError('');
    if (!nextUser) {
      setAuthLoading(false);
      return;
    }
    try {
      const result = await provisionUser();
      // Claims were changed by the server during provisioning/approval.
      await nextUser.getIdToken(true);
      setAccess(result.data);
    } catch (error) {
      setAuthError(messageFrom(error));
    } finally {
      setAuthLoading(false);
    }
  }), []);

  async function signIn() {
    setAuthLoading(true);
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      setAuthError(messageFrom(error));
      setAuthLoading(false);
    }
  }

  if (authLoading) return <LoadingScreen />;
  if (!user) return <LandingPage error={authError} onSignIn={signIn} />;
  if (!access || access.status !== 'approved') return <WaitingRoom user={user} access={access} error={authError} />;
  return <LearningWorkspace user={user} access={access} />;
}

function LoadingScreen() {
  return <main className="gate-shell"><div className="loading-mark"><StudiumEmblem size={32} /> <span>Studium</span></div><p>טוען סביבת שליטה מקצועית…</p></main>;
}

function WaitingRoom({ user, access, error }: { user: User; access: UserAccess | null; error: string }) {
  const blocked = access?.status === 'blocked';
  return (
    <main className="gate-shell">
      <section className="waiting-card">
        <div className={`status-icon ${blocked ? 'blocked' : ''}`}>{blocked ? <XCircle /> : <UserCheck />}</div>
        <span className="eyebrow">סטודיום · בקרת גישה</span>
        <h1>{blocked ? 'הגישה לחשבון נחסמה' : 'הבקשה שלך התקבלה'}</h1>
        {/* Reaching this screen at all now means one of two deliberate acts:
            the super admin closed open joining, or this account was blocked.
            Each gets its own sentence, because they are not the same news. */}
        <p>{blocked
          ? 'פנה לסופר אדמין כדי לבקש פתיחה מחדש של הגישה.'
          : `ההצטרפות החופשית סגורה כרגע, ולכן החשבון ${user.email} ממתין לאישור הסופר אדמין. לאחר האישור אפשר לרענן את הדף ולהתחיל ללמוד.`}</p>
        {error && <p className="form-error">{error}</p>}
        <div className="waiting-actions">
          <button className="secondary-button" onClick={() => window.location.reload()}><RefreshCw size={16} /> בדוק שוב</button>
          <button className="secondary-button" onClick={() => signOut(auth)}><LogOut size={16} /> החלפת חשבון</button>
        </div>
      </section>
    </main>
  );
}

function LearningWorkspace({ user, access }: { user: User; access: UserAccess }) {
  const [view, setView] = useState<View>('overview');
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentError, setContentError] = useState('');
  const [journey, setJourney] = useState<LearningJourney | null>(null);
  const [learningBusy, setLearningBusy] = useState(false);
  const [modelCatalog, setModelCatalog] = useState<ModelCatalog | null>(null);
  const [modelsError, setModelsError] = useState('');
  // The main navigation collapses to a rail of glyphs, the same curtain idea as
  // the unit list. Remembered so the choice survives a reload.
  // The acknowledged version is server truth at load; acknowledging in place
  // updates it without a reload.
  const [policyVersion, setPolicyVersion] = useState(access.privacyPolicyVersion);
  const policyOutdated = Boolean(access.currentPrivacyPolicyVersion) && policyVersion !== access.currentPrivacyPolicyVersion;
  const [navOpen, setNavOpen] = useState(() => localStorage.getItem('studium.nav') !== 'closed');
  useEffect(() => { localStorage.setItem('studium.nav', navOpen ? 'open' : 'closed'); }, [navOpen]);
  // The server, not the browser email string, is the authority for this role.
  const isSuperAdmin = access.role === 'super_admin';

  useEffect(() => {
    let alive = true;
    Promise.all([getKnowledgeIndex(), getLearningJourney()]).then(([knowledgeResult, journeyResult]) => {
      if (!alive) return;
      setModules(knowledgeResult.data);
      setSelectedModule(knowledgeResult.data[0] ?? null);
      setJourney(journeyResult.data);
    }).catch((error) => alive && setContentError(messageFrom(error)));
    // The assistant needs to know which model is active before the first
    // question, so the catalog loads with the workspace rather than waiting for
    // someone to press a button. A missing key surfaces as the inline notice.
    void getModelCatalog()
      .then((result) => { if (alive) setModelCatalog(result.data); })
      .catch((error) => { if (alive) setModelsError(messageFrom(error)); });
    return () => { alive = false; };
  }, []);

  // A mutation returns the authoritative profile and unit state. The derived
  // parts of the journey — rank, insignia, campaign unlocks — are computed on
  // the server from evidence it holds, so they are carried over untouched here
  // and refreshed from the source rather than re-derived in the browser.
  const applyLearningMutation = useCallback((mutation: LearningMutation) => {
    setJourney((current) => {
      if (!current) return current;
      const states = [...current.states.filter((state) => state.moduleId !== mutation.state.moduleId), mutation.state];
      return { ...current, profile: mutation.profile, states };
    });
    setLesson((current) => current?.id === mutation.state.moduleId ? { ...current, learningState: mutation.state } : current);
    void getLearningJourney().then((result) => setJourney(result.data)).catch(() => undefined);
  }, []);

  async function openLesson(module: CourseModule) {
    setSelectedModule(module);
    setView('curriculum');
    setLoadingContent(true);
    setContentError('');
    try {
      const result = await getLesson(module.id);
      setLesson(result.data);
      setJourney((current) => current ? {
        ...current,
        states: [...current.states.filter((state) => state.moduleId !== module.id), result.data.learningState],
      } : current);
    } catch (error) {
      setContentError(messageFrom(error));
    } finally {
      setLoadingContent(false);
    }
  }

  function openCurriculum() {
    if (!lesson && modules[0]) {
      void openLesson(modules[0]);
      return;
    }
    setView('curriculum');
  }

  const saveStage = useCallback(async (stageId: GameStageId, action: 'open_stage' | 'save_draft' | 'complete_stage', draft?: string) => {
    if (!lesson) throw new Error('יש לבחור יחידת לימוד תחילה.');
    setLearningBusy(true);
    try {
      const result = await saveLearningProgress({ moduleId: lesson.id, action, stageId, draft });
      applyLearningMutation(result.data);
    } catch (error) { throw new Error(messageFrom(error)); }
    finally { setLearningBusy(false); }
  }, [applyLearningMutation, lesson]);

  const resetStage = useCallback(async (scope: 'question' | 'stage' | 'lesson', stageId?: GameStageId, challengeId?: string) => {
    if (!lesson) throw new Error('יש לבחור יחידת לימוד תחילה.');
    setLearningBusy(true);
    try {
      const result = await resetLearningProgress({ moduleId: lesson.id, scope, stageId, challengeId });
      applyLearningMutation(result.data);
    } catch (error) { throw new Error(messageFrom(error)); }
    finally { setLearningBusy(false); }
  }, [applyLearningMutation, lesson]);

  const answerChallenge = useCallback(async (challengeId: string, optionId: string): Promise<LearningChallengeResult> => {
    if (!lesson) throw new Error('יש לבחור יחידת לימוד תחילה.');
    setLearningBusy(true);
    try {
      const result = await answerLearningChallenge({ moduleId: lesson.id, challengeId, optionId });
      applyLearningMutation(result.data);
      return result.data;
    } catch (error) { throw new Error(messageFrom(error)); }
    finally { setLearningBusy(false); }
  }, [applyLearningMutation, lesson]);

  async function loadModels() {
    setModelsError('');
    try {
      const result = await getModelCatalog();
      setModelCatalog(result.data);
    } catch (error) {
      setModelsError(messageFrom(error));
    }
  }

  const completed = journey?.profile.masteredModuleIds ?? [];
  // Progress is reported by the server in stages completed out of all stages.
  // Counting only fully mastered units showed 0% to someone most of the way
  // through twenty of them.
  const progress = journey?.progress.percent ?? 0;

  const resumeLearning = useCallback(async () => {
    const target = journey?.resume;
    if (!target) { openCurriculum(); return; }
    const module = modules.find((item) => item.id === target.unitId);
    if (module) await openLesson(module);
  }, [journey?.resume, modules]);
  const page = view === 'overview'
    ? <Overview modules={modules} completed={completed} progress={progress} journey={journey} onOpen={openLesson} onResume={resumeLearning} onViewChange={(nextView) => nextView === 'curriculum' ? openCurriculum() : setView(nextView)} />
    : view === 'curriculum'
      ? <Curriculum modules={modules} selected={selectedModule} lesson={lesson} completed={completed} loading={loadingContent} error={contentError} learningBusy={learningBusy} onOpen={openLesson} onOpenStage={(stageId) => saveStage(stageId, 'open_stage')} onSaveDraft={(stageId, draft) => saveStage(stageId, 'save_draft', draft)} onCompleteStage={(stageId) => saveStage(stageId, 'complete_stage')} onReset={resetStage} onAnswerChallenge={answerChallenge} />
      : view === 'assistant'
        ? <Assistant catalog={modelCatalog} modelsError={modelsError} onLoadModels={loadModels} onConnect={() => setView('ai')} />
        : view === 'ai'
          ? <AiManagementPanel isSuperAdmin={isSuperAdmin} onCatalogChange={setModelCatalog} />
          : view === 'privacy'
            ? <Suspense fallback={<div className="reader-loading compact-loading"><RefreshCw className="spin" /> טוען את מרכז הפרטיות…</div>}><PrivacyCenter isSuperAdmin={isSuperAdmin} onDeleted={() => signOut(auth)} /></Suspense>
        : <AdminPanel enabled={isSuperAdmin} />;

  return (
    <div className={`app-shell ${navOpen ? '' : 'nav-collapsed'}`}>
      <aside className="sidebar">
        <div className="sidebar-head">
          <div className="brand-signature brand-signature-sidebar"><StudiumWordmark className="workspace-wordmark" /></div>
          <button
            className="nav-toggle"
            onClick={() => setNavOpen((open) => !open)}
            aria-expanded={navOpen}
            aria-label={navOpen ? 'הסתרת תפריט הניווט' : 'הצגת תפריט הניווט'}
            title={navOpen ? 'הסתר תפריט' : 'הצג תפריט'}
          >
            {navOpen ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>
        <nav>
          <NavItem active={view === 'overview'} icon={<LayoutDashboard />} label="תמונת מצב" onClick={() => setView('overview')} />
          <NavItem active={view === 'curriculum'} icon={<GraduationCap />} label="מסלול הלמידה" onClick={openCurriculum} />
          <NavItem active={view === 'assistant'} icon={<Bot />} label="עוזר מקצועי" onClick={() => setView('assistant')} />
          <NavItem active={view === 'ai'} icon={<KeyRound />} label={isSuperAdmin ? 'ניהול AI' : 'הגדרות AI'} onClick={() => setView('ai')} />
          <NavItem active={view === 'privacy'} icon={<ShieldCheck />} label="פרטיות ונתונים" onClick={() => setView('privacy')} />
          {isSuperAdmin && <NavItem active={view === 'admin'} icon={<UsersRound />} label="ניהול גישה" onClick={() => setView('admin')} />}
        </nav>
        <div className="sidebar-footer">
          <div className="security-pulse"><ShieldCheck size={15} /> גישה מאושרת</div>
          <div className="user-chip"><span className="avatar">{initials(user.displayName || user.email)}</span><div><strong>{user.displayName || user.email || 'לומד/ת מאושר/ת'}</strong><small>{isSuperAdmin ? 'סופר אדמין' : 'לומד מאושר'}</small></div><button aria-label="התנתקות" onClick={() => signOut(auth)}><LogOut size={16} /></button></div>
        </div>
      </aside>
      <main className="main-canvas">
        <header className="topbar"><div><span className="breadcrumb">סטודיום / {view === 'overview' ? 'תמונת מצב' : view === 'curriculum' ? 'מסלול הלמידה' : view === 'assistant' ? 'עוזר מקצועי' : view === 'ai' ? (isSuperAdmin ? 'ניהול AI' : 'הגדרות AI') : view === 'privacy' ? 'פרטיות ונתונים' : 'ניהול גישה'}</span></div><div className="topbar-actions"><div className="topbar-identity"><span className="avatar top-avatar">{initials(user.displayName || user.email)}</span><strong>{fullName(user.displayName, user.email)}</strong></div><button className="secondary-button topbar-signout" onClick={() => signOut(auth)}><LogOut size={15} /> התנתק</button></div></header>
        {policyOutdated && <PolicyConsentBanner version={access.currentPrivacyPolicyVersion} onAcknowledged={setPolicyVersion} onRead={() => setView('privacy')} />}
        {page}
      </main>
    </div>
  );
}

/** Consent is to a specific text. When the text changes, the previous consent
    no longer covers it, so the learner is asked again before carrying on. */
function PolicyConsentBanner({ version, onAcknowledged, onRead }: { version: string; onAcknowledged: (version: string) => void; onRead: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function confirm() {
    setBusy(true);
    setError('');
    try {
      const result = await acknowledgePrivacyPolicy(version);
      onAcknowledged(result.data.version);
    } catch (caught) {
      setError(messageFrom(caught));
      setBusy(false);
    }
  }
  return (
    <div className="policy-banner">
      <ShieldCheck size={18} />
      <div>
        <strong>מדיניות הפרטיות עודכנה</strong>
        <p>גרסה {version}. קרא את הנוסח החדש ואשר אותו כדי להמשיך להשתמש במערכת.</p>
        {error && <p className="form-error">{error}</p>}
      </div>
      <div className="policy-banner-actions">
        <button className="secondary-button" onClick={onRead}>קריאת המדיניות</button>
        <button className="primary-button" onClick={confirm} disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Check size={16} />} אישור</button>
      </div>
    </div>
  );
}

function NavItem({ active, icon, label, onClick }: { active: boolean; icon: React.ReactNode; label: string; onClick: () => void }) {
  // `title` carries the name when the rail is collapsed to glyphs only.
  return <button className={`nav-item ${active ? 'active' : ''}`} onClick={onClick} title={label}>{icon}<span>{label}</span></button>;
}

function Overview({ modules, completed, progress, journey, onOpen, onResume, onViewChange }: { modules: CourseModule[]; completed: string[]; progress: number; journey: LearningJourney | null; onOpen: (module: CourseModule) => void; onResume: () => void; onViewChange: (view: View) => void }) {
  const profile = journey?.profile;
  const next = modules.find((module) => !completed.includes(module.id)) ?? modules[modules.length - 1];
  const completedCount = completed.length;
  const labsDone = modules.filter((module) => module.type === 'lab' && completed.includes(module.id)).length;
  const weekly = Array.from({ length: 8 }, (_, index) => {
    const week = index + 1;
    const units = modules.filter((module) => module.week === week);
    return { week, total: units.length, done: units.filter((module) => completed.includes(module.id)).length, theme: weekTheme(week) };
  });
  const levelProgress = journey?.levelProgress;
  const levelRatio = levelProgress ? Math.round((levelProgress.xpIntoLevel / levelProgress.xpPerLevel) * 100) : 0;
  return <section className="page page-overview">
    <div className="hero-row">
      <div className="hero-copy"><span className="eyebrow">תמונת מצב מקצועית</span><h1>הדרך שלך להוביל <em>AI אמין</em><br />במערכת מורכבת.</h1><p>המסלול מחבר שליטה בנתונים, שכבה סמנטית, Text-to-SQL, סוכנים, אבטחה והובלה מוצרית — לנכס מקצועי אחד.</p></div>
      <div className="progress-orbit"><div className="orbit-ring" style={{ '--progress': `${progress * 3.6}deg` } as React.CSSProperties}><strong>{progress}%</strong><span>הושלם</span></div><small>{journey ? `${journey.progress.completedStages} מתוך ${journey.progress.totalStages} שלבים · ${journey.progress.masteredUnits} יחידות הושלמו` : `${completedCount} מתוך ${modules.length} יחידות`}</small></div>
    </div>

    {journey && <section className="rank-banner">
      <div className="rank-identity">
        <span className="eyebrow">הדרגה שלך</span>
        <h2>{journey.rank.title}</h2>
        <p>{journey.rank.note}</p>
      </div>
      <div className="rank-meter">
        <div className="rank-meter-head">
          <span>רמה {profile?.level ?? 1}</span>
          {journey.nextRank && <span className="rank-next">הדרגה הבאה: {journey.nextRank.title} · רמה {journey.nextRank.minLevel}</span>}
        </div>
        <div className="rank-track"><span style={{ width: `${levelRatio}%` }} /></div>
        <small>{levelProgress?.xpIntoLevel ?? 0}/{levelProgress?.xpPerLevel ?? 0} XP לרמה הבאה</small>
      </div>
      <div className="rank-insignia">
        <span className="eyebrow">אותות</span>
        <div className="insignia-row">
          {journey.insignia.map((item) => {
            const Glyph = INSIGNIA_GLYPHS[item.id];
            return <span key={item.id} className={`insignia ${item.earned ? 'earned' : 'locked'}`} title={`${item.title} — ${item.requirement}`}>
              {Glyph ? <Glyph size={20} /> : null}
            </span>;
          })}
        </div>
        <small>{journey.insignia.filter((item) => item.earned).length}/{journey.insignia.length} הושגו</small>
      </div>
    </section>}

    <div className="stat-grid">
      <Stat icon={<BookOpen />} label="יחידות במסלול" value={`${modules.length}`} detail={journey ? `${journey.academy.concepts} מושגים · ${journey.academy.questions} שאלות` : '32 שיעורים · 8 מעבדות'} tone="indigo" />
      <Stat icon={<CheckCircle2 />} label="הושלמו" value={`${completedCount}`} detail="שערי שליטה שעברו" tone="mint" />
      <Stat icon={<Layers3 />} label="מעבדות" value={`${labsDone}/8`} detail="תוצרים מצטברים" tone="amber" />
      <Stat icon={<ShieldCheck />} label="דיוק בניסיון ראשון" value={journey?.accuracy === null || journey?.accuracy === undefined ? '—' : `${journey.accuracy}%`} detail={`${profile?.questionsAnswered ?? 0} שאלות · רצף ${profile?.streakDays ?? 0} ימים`} tone="rose" />
    </div>
    {journey?.resume && <section className="continue-card resume-card">
      <div className="continue-icon"><Target /></div>
      <div>
        <span className="eyebrow">להמשיך מאיפה שהפסקת</span>
        <h2>{journey.resume.unitTitle}</h2>
        <p>עצרת ב“{journey.resume.stageLabel}” · יחידה {journey.resume.unitNumber} · שבוע {journey.resume.week}</p>
        <div className="module-tags"><span>{relativeDay(journey.resume.updatedAtMs)}</span><span>{journey.progress.answeredQuestions}/{journey.progress.totalQuestions} שאלות נענו</span></div>
      </div>
      <button className="primary-button" onClick={onResume}>חזרה ללמידה <ArrowLeft size={17} /></button>
    </section>}
    {next && !journey?.resume && <section className="continue-card"><div className="continue-icon"><Target /></div><div><span className="eyebrow">הצעד הבא שלך</span><h2>{next.title}</h2><p>{next.summary}</p><div className="module-tags"><span>שבוע {next.week}</span><span>{next.type === 'lab' ? 'מעבדה מעשית' : 'שיעור עומק'}</span></div></div><button className="primary-button" onClick={() => onOpen(next)}>פתיחת היחידה <ArrowLeft size={17} /></button></section>}
    <div className="split-grid">
      <section className="panel journey-panel"><div className="panel-title"><div><span className="eyebrow">מסלול למידה</span><h2>שמונה שבועות, יכולת אחת רציפה</h2></div><button className="text-button" onClick={() => onViewChange('curriculum')}>לכל המסלול <ChevronLeft size={16} /></button></div><div className="week-track">{weekly.map((week) => <button key={week.week} className={`week-dot ${week.done === week.total ? 'done' : ''}`} onClick={() => onViewChange('curriculum')}><span className={`dot-color ${week.theme}`}><Check size={14} /></span><div><strong>שבוע {week.week}</strong><small>{week.done}/{week.total} · {weekThemeLabel(week.week)}</small></div></button>)}</div></section>
      <section className="panel advisor-panel"><div className="advisor-glow" /><div className="panel-title"><div><span className="eyebrow">עוזר מקצועי</span><h2>מחשבה מבוססת מקור</h2></div><Bot size={22} /></div><p>שאל על מושג, התלבטות מקצועית או תרגיל. העוזר שולף הקשר קנוני, מציין מקור, ומבדיל בין עובדה, הנחה והמלצה.</p><div className="prompt-suggestions"><button onClick={() => onViewChange('assistant')}>איך בוחנים שכבה סמנטית?</button><button onClick={() => onViewChange('assistant')}>איך מתכננים Eval אמין?</button></div><button className="secondary-button full" onClick={() => onViewChange('assistant')}>פתיחת העוזר <MessageSquareText size={16} /></button></section>
    </div>
  </section>;
}

function Stat({ icon, label, value, detail, tone }: { icon: React.ReactNode; label: string; value: string; detail: string; tone: string }) {
  return <article className={`stat-card ${tone}`}><span className="stat-icon">{icon}</span><div><small>{label}</small><strong>{value}</strong><span>{detail}</span></div></article>;
}

function Curriculum({ modules, selected, lesson, completed, loading, error, learningBusy, onOpen, onOpenStage, onSaveDraft, onCompleteStage, onReset, onAnswerChallenge }: {
  modules: CourseModule[]; selected: CourseModule | null; lesson: Lesson | null; completed: string[]; loading: boolean; error: string; learningBusy: boolean;
  onOpen: (module: CourseModule) => void;
  onOpenStage: (stageId: GameStageId) => Promise<void>;
  onSaveDraft: (stageId: GameStageId, draft: string) => Promise<void>;
  onCompleteStage: (stageId: GameStageId) => Promise<void>;
  onReset: (scope: 'question' | 'stage' | 'lesson', stageId?: GameStageId, challengeId?: string) => Promise<void>;
  onAnswerChallenge: (challengeId: string, optionId: string) => Promise<LearningChallengeResult>;
}) {
  const [moduleFilter, setModuleFilter] = useState('');
  const [readerMode, setReaderMode] = useState<'quest' | 'source'>('quest');
  // The unit list is a curtain: it slides aside and the reading column takes
  // the space. Remembered so the choice survives a reload.
  const [browserOpen, setBrowserOpen] = useState(() => localStorage.getItem('studium.unitBrowser') !== 'closed');
  useEffect(() => { localStorage.setItem('studium.unitBrowser', browserOpen ? 'open' : 'closed'); }, [browserOpen]);
  const filtered = modules.filter((module) => `${module.title} ${module.summary}`.toLowerCase().includes(moduleFilter.toLowerCase()));

  useEffect(() => { setReaderMode('quest'); }, [selected?.id]);

  return <section className="page curriculum-page">
    <div className="page-heading"><div><span className="eyebrow">הספרייה הקנונית</span><h1>מסלול הלמידה</h1><p>40 יחידות, שמונה שבועות, תוצרים מצטברים, שלבי משחק ושערי שליטה.</p></div><div className="search-box"><span>⌕</span><input value={moduleFilter} onChange={(event) => setModuleFilter(event.target.value)} placeholder="סינון יחידות במסלול" /></div></div>
    <Suspense fallback={<div className="panel-inline-loading"><RefreshCw className="spin" size={16} /> מכין חיפוש קנוני…</div>}><KnowledgeSearch selectedModuleId={selected?.id} onOpenModule={(id) => { const module = modules.find((item) => item.id === id); if (module) onOpen(module); }} /></Suspense>
    <div className={`curriculum-layout ${browserOpen ? '' : 'browser-collapsed'}`}>
      <button
        className="browser-toggle"
        onClick={() => setBrowserOpen((open) => !open)}
        aria-expanded={browserOpen}
        aria-controls="unit-browser"
        title={browserOpen ? 'הסתרת רשימת היחידות' : 'הצגת רשימת היחידות'}
      >
        {browserOpen ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}
        <span>{browserOpen ? 'הסתר יחידות' : `יחידות (${filtered.length})`}</span>
      </button>
      <aside className="module-browser" id="unit-browser" aria-hidden={!browserOpen}>{Array.from({ length: 8 }, (_, index) => index + 1).map((week) => { const weekModules = filtered.filter((module) => module.week === week); if (!weekModules.length) return null; return <div className="week-group" key={week}><h3><span>שבוע {week}</span><small>{weekThemeLabel(week)}</small></h3>{weekModules.map((module) => <button key={module.id} className={`module-row ${selected?.id === module.id ? 'selected' : ''}`} onClick={() => onOpen(module)}><span className={`row-status ${completed.includes(module.id) ? 'complete' : ''}`}>{completed.includes(module.id) ? <Check size={13} /> : module.number}</span><span><strong>{module.title.replace(/^שיעור \d+:\s*/, '')}</strong><small>{module.type === 'lab' ? 'מעבדה · תוצר מצטבר' : 'שיעור עומק'}</small></span></button>)}</div>; })}</aside>
      <article className="lesson-reader">{loading ? <div className="reader-loading"><RefreshCw className="spin" /> טוען את היחידה הקנונית…</div> : error ? <div className="reader-empty error"><XCircle /><h2>לא ניתן לטעון את התוכן</h2><p>{error}</p></div> : lesson && selected ? <><header className="lesson-header"><div><div className="module-tags"><span>שבוע {selected.week}</span><span>{selected.type === 'lab' ? 'מעבדה' : 'שיעור'}</span><span>{selected.id}</span>{lesson.learningState.status === 'mastered' && <span className="mastered-tag"><CheckCircle2 size={12} /> שליטה הושגה</span>}</div><h1>{lesson.title}</h1><p>{selected.summary}</p></div><div className="lesson-reader-tabs"><button className={readerMode === 'quest' ? 'active' : ''} onClick={() => setReaderMode('quest')}><Sparkles size={16} /> ללמוד ולתרגל</button><button className={readerMode === 'source' ? 'active' : ''} onClick={() => setReaderMode('source')}><FileText size={16} /> קובץ המקור</button></div></header>{readerMode === 'quest' ? <Suspense fallback={<div className="reader-loading compact-loading"><RefreshCw className="spin" /> בונה מסלול לימוד…</div>}><LearningQuest lesson={lesson} state={lesson.learningState} busy={learningBusy} onOpenStage={onOpenStage} onSaveDraft={onSaveDraft} onCompleteStage={onCompleteStage} onReset={onReset} onAnswerChallenge={onAnswerChallenge} onShowSource={() => setReaderMode('source')} /></Suspense> : <><div className="canonical-file-bar"><FileText size={16} /><span>קובץ קנוני: <strong>{lesson.path}</strong></span><small>הקריאה כאן היא בתוכן המקורי המדויק שעליו מבוססת המשימה.</small></div><Suspense fallback={<div className="reader-loading compact-loading"><RefreshCw className="spin" /> מכין תצוגת קריאה…</div>}><MarkdownLesson markdown={lesson.markdown} /></Suspense></>}</> : <div className="reader-empty"><BookOpen /><h2>בחר יחידת לימוד</h2><p>כל יחידה כוללת מסלול לימוד, בדיקות הבנה, תרגול ותיק למידה אישי.</p></div>}</article>
    </div>
  </section>;
}

function relativeDay(ms: number) {
  if (!ms) return '';
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  if (days <= 0) return 'היום';
  if (days === 1) return 'אתמול';
  if (days < 7) return `לפני ${days} ימים`;
  return new Date(ms).toLocaleDateString('he-IL');
}

function Assistant({ catalog, modelsError, onLoadModels, onConnect }: { catalog: ModelCatalog | null; modelsError: string; onLoadModels: () => void; onConnect: () => void }) {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [conversationId, setConversationId] = useState('');
  const [loadingThread, setLoadingThread] = useState(false);
  const models = catalog?.models ?? [];
  const configuration = catalog?.configuration;
  const selected = models.find((model) => model.id === configuration?.selectedModelId);
  const autoRouted = configuration?.selectionMode === 'auto' || !configuration?.selectedModelId;

  const refreshHistory = useCallback(async () => {
    try {
      setConversations((await listConversations()).data.conversations);
    } catch {
      // History is a convenience; a failure here must not block asking.
    }
  }, []);

  useEffect(() => { void refreshHistory(); }, [refreshHistory]);

  async function openConversation(id: string) {
    setLoadingThread(true); setError('');
    try {
      const thread = (await getConversation(id)).data;
      setConversationId(thread.id);
      setMessages(thread.turns.map((turn, index) => ({
        id: `${thread.id}-${index}`,
        role: turn.role,
        content: turn.content,
      })));
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setLoadingThread(false); }
  }

  function startNew() {
    setConversationId(''); setMessages([]); setError(''); setQuestion('');
  }

  async function removeConversation(id: string) {
    if (!window.confirm('למחוק את השיחה הזו לצמיתות?')) return;
    try {
      await deleteConversation(id);
      if (id === conversationId) startNew();
      await refreshHistory();
    } catch (cause) { setError(messageFrom(cause)); }
  }

  async function send(event?: React.FormEvent, suggested?: string) {
    event?.preventDefault();
    const nextQuestion = (suggested ?? question).trim();
    if (!nextQuestion || sending) return;
    const userMessage: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: nextQuestion };
    setMessages((current) => [...current, userMessage]);
    setQuestion(''); setSending(true); setError('');
    try {
      const result = await askAssistant({ question: nextQuestion, conversationId: conversationId || undefined });
      setMessages((current) => [...current, { id: crypto.randomUUID(), role: 'assistant', content: result.data.answer, result: result.data }]);
      setConversationId(result.data.conversationId);
      await refreshHistory();
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setSending(false); }
  }

  /* The assistant is the one part of Studium that needs an OpenRouter account.
     Everything else — the units, the quizzes, the practice, the portfolio — runs
     without one, so a missing key is answered with an explanation and a way
     forward rather than with an error on an otherwise empty screen. */
  if (catalog && !catalog.configuration.keyConfigured) {
    return <section className="page assistant-page">
      <div className="page-heading assistant-heading"><div><span className="eyebrow">עוזר מקצועי מבוסס מקור</span><h1>בוא נחשוב את זה לעומק.</h1><p>קבל הסבר, בדיקת תרגול או מסגרת קבלת החלטות — עם הקשר ממאגר הידע הקנוני.</p></div><div className="ai-badge"><Sparkles size={17} /> ידע · ראיות · פעולה</div></div>
      <section className="connect-gate">
        <span className="connect-gate-mark"><Bot size={26} /></span>
        <h2>העוזר ממתין לחיבור OpenRouter אישי</h2>
        <p>העוזר הוא החלק היחיד שמבוסס על מודל שפה, ולכן הוא זקוק לחשבון OpenRouter משלך. הוא ירוץ על המפתח שלך בלבד, על חשבונך, ואף משתמש אחר לא נוגע בו.</p>
        <div className="connect-gate-open">
          <strong>כל השאר פתוח לפניך כבר עכשיו</strong>
          <p>ארבעים יחידות הלימוד, בדיקות ההבנה, המקרים המפוענחים, זירת התרגול, שער השליטה ותיק ההחלטות — כולם עובדים במלואם בלי שום חיבור חיצוני. ההתקדמות, ה־XP והדרגות נצברים כרגיל.</p>
        </div>
        <div className="connect-gate-actions">
          <button className="primary-button" onClick={onConnect}><KeyRound size={16} /> חיבור OpenRouter</button>
          <button className="secondary-button" onClick={onLoadModels}><RefreshCw size={16} /> כבר חיברתי — בדוק שוב</button>
        </div>
        <small className="security-note"><ShieldCheck size={14} /> המפתח מוצפן ב־Cloud KMS, נשמר מחוץ ל־Firestore, אינו מוצג מחדש ואינו משותף עם אף חשבון אחר.</small>
      </section>
    </section>;
  }

  return <section className="page assistant-page">
    <div className="page-heading assistant-heading"><div><span className="eyebrow">עוזר מקצועי מבוסס מקור</span><h1>בוא נחשוב את זה לעומק.</h1><p>קבל הסבר, בדיקת תרגול או מסגרת קבלת החלטות — עם הקשר ממאגר הידע הקנוני.</p></div><div className="ai-badge"><Sparkles size={17} /> ידע · ראיות · פעולה</div></div>
    <div className="assistant-layout">
      <aside className="model-panel">
        <div className="panel-title"><div><span className="eyebrow">שיחות</span><h2>היסטוריה אישית</h2></div><MessageSquareText size={20} /></div>
        <button className="secondary-button full" onClick={startNew}><Sparkles size={16} /> שיחה חדשה</button>
        <div className="conversation-list">
          {conversations.length === 0
            ? <p className="conversation-empty">עוד לא נוהלו שיחות. כל שיחה תישמר כאן ותוכל להמשיך אותה בכל עת.</p>
            : conversations.map((item) => <div key={item.id} className={`conversation-item ${item.id === conversationId ? 'active' : ''}`}>
                <button className="conversation-open" onClick={() => void openConversation(item.id)}>
                  <strong>{item.title}</strong>
                  <small>{relativeDay(item.updatedAtMs)} · {item.turnCount} הודעות</small>
                </button>
                <button className="conversation-delete" onClick={() => void removeConversation(item.id)} aria-label={`מחיקת השיחה ${item.title}`}><XCircle size={15} /></button>
              </div>)}
        </div>
        <div className="panel-title compact"><div><span className="eyebrow">מנוע החשיבה</span><h2>המודל הפעיל</h2></div><Cpu size={20} /></div>
        {models.length === 0
          ? <button className="secondary-button full" onClick={onLoadModels}><RefreshCw size={16} /> טעינת מצב AI</button>
          : <>
              <div className="active-model-box">
                <small>{autoRouted ? 'ניתוב אוטומטי' : 'מודל קבוע'}</small>
                <strong>{autoRouted ? 'נבחר לפי הקשר ויכולת' : selected?.name || 'טרם נבחר מודל'}</strong>
                {selected && !autoRouted && <span>{selected.id}</span>}
              </div>
              {selected && !autoRouted && <div className="model-facts"><span><small>קלט</small><strong>{formatPrice(selected.inputPricePerMillion)}</strong></span><span><small>פלט</small><strong>{formatPrice(selected.outputPricePerMillion)}</strong></span><span><small>הקשר</small><strong>{Math.round(selected.contextLength / 1000)}K</strong></span></div>}
            </>}
        {modelsError && <div className="inline-warning"><ShieldCheck size={16} /><span>{modelsError.includes('מפתח OpenRouter') ? 'יש להשלים מפתח OpenRouter אישי בהגדרות AI.' : modelsError}</span></div>}
        <div className="source-contract"><FileText size={17} /><p><strong>כלל תשובה</strong>העוזר מחזיר מקור, שואל הבהרה כשצריך, ואינו מציג חומר סינתטי כמידע ארגוני חי. עומק התשובה נגזר מהשאלה עצמה.</p></div>
      </aside>
      <div className="chat-panel"><div className="chat-header"><div className="assistant-avatar"><Bot size={19} /></div><div><strong>עוזר הלמידה של Studium</strong><small><span className="online-dot" /> {conversationId ? 'ממשיך שיחה קיימת' : 'בהמתנה לשאלה שלך'}</small></div></div><div className="chat-body">{loadingThread && <div className="reader-loading compact-loading"><RefreshCw className="spin" /> טוען את השיחה…</div>}{!loadingThread && messages.length === 0 && <div className="empty-chat"><div className="empty-spark"><Sparkles /></div><h2>על מה נחשוב היום?</h2><p>אפשר להתחיל משאלה על המסלול, על דילמה מקצועית או מתרגיל שאתה רוצה לבדוק.</p><div className="starter-grid"><button onClick={() => send(undefined, 'איך אבחן האם שכבה סמנטית באמת מוכנה ל-Text-to-SQL?')}>איך בוחנים שכבה סמנטית?</button><button onClick={() => send(undefined, 'בנה לי מסגרת לקבלת החלטה בין דיוק, עלות ו-latency בסוכן.')}>דיוק מול עלות ו-latency</button><button onClick={() => send(undefined, 'בחן אותי על שלבי Text-to-SQL והמתן לתשובה שלי.')}>בחינה על Text-to-SQL</button></div></div>}{messages.map((message) => <div className={`chat-message ${message.role}`} key={message.id}><div className="message-label">{message.role === 'user' ? 'אתה' : 'Studium'}</div><div className="message-bubble">{message.content}{message.result && <div className="answer-meta"><span><Cpu size={13} /> {message.result.model.name}</span>{message.result.references.slice(0, 3).map((reference) => <span key={`${reference.moduleId}-${reference.heading}`}><FileText size={13} /> {reference.moduleId}</span>)}</div>}</div></div>)}{sending && <div className="typing"><span /><span /><span /></div>}</div><form className="composer" onSubmit={send}><textarea value={question} onChange={(event) => setQuestion(event.target.value)} placeholder={conversationId ? 'המשך את השיחה…' : 'שאל שאלה, בקש הסבר או הדבק תשובה לבדיקה…'} rows={2} /><button disabled={!question.trim() || sending} aria-label="שליחת שאלה"><Send size={18} /></button></form>{error && <p className="form-error chat-error">{error}</p>}</div>
    </div>
  </section>;
}

function AiManagementPanel({ isSuperAdmin, onCatalogChange }: { isSuperAdmin: boolean; onCatalogChange: (catalog: ModelCatalog) => void }) {
  const [settings, setSettings] = useState<AiConfiguration | null>(null);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [apiKey, setApiKey] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectionMode, setSelectionMode] = useState<'fixed' | 'auto'>('fixed');
  const [query, setQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [sortBy, setSortBy] = useState<ModelSort>('price-asc');
  const [freeOnly, setFreeOnly] = useState(false);
  const [toolsOnly, setToolsOnly] = useState(false);
  const [structuredOnly, setStructuredOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [testing, setTesting] = useState(false);
  const [replacingKey, setReplacingKey] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [testResult, setTestResult] = useState<AiConnectionTest | null>(null);
  const [testError, setTestError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  function applyCatalog(catalog: ModelCatalog) {
    setSettings(catalog.configuration);
    setReplacingKey(false);
    setModels(catalog.models);
    setSelectedModelId(catalog.configuration.selectedModelId);
    setSelectionMode(catalog.configuration.selectionMode);
    onCatalogChange(catalog);
  }

  // Opening this screen with a key already connected has to arrive with the
  // catalog in hand. Loading only the configuration left the picker showing
  // "0 models" next to a connection that had just reported 402 of them, with
  // no way to load them short of finding the refresh button in the header.
  async function refreshSettings() {
    setLoading(true); setError(''); setCatalogError('');
    try {
      const result = await getAiSettings();
      setSettings(result.data);
      setSelectedModelId(result.data.selectedModelId);
      setSelectionMode(result.data.selectionMode);
      if (result.data.keyConfigured) await loadCatalog();
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setLoading(false); }
  }

  // Catalog failures are reported separately: the key can be perfectly valid
  // while OpenRouter is briefly unreachable, and that must not read as a
  // broken connection.
  async function loadCatalog() {
    setLoadingCatalog(true); setCatalogError('');
    try {
      applyCatalog((await getModelCatalog()).data);
    } catch (cause) {
      setCatalogError(messageFrom(cause));
    } finally {
      setLoadingCatalog(false);
    }
  }

  async function refreshCatalog() {
    setError('');
    await loadCatalog();
  }

  useEffect(() => { void refreshSettings(); }, []);

  async function saveKey() {
    if (!apiKey.trim()) { setError('יש להזין מפתח OpenRouter לפני שמירת החיבור.'); return; }
    setSavingKey(true); setError(''); setNotice('');
    try {
      applyCatalog((await configureAi({ apiKey: apiKey.trim() })).data);
      setApiKey('');
      setTestResult(null); setTestError('');
      // Saving a key now leaves the account usable straight away: routing is
      // automatic until a fixed model is chosen deliberately.
      setNotice(isSuperAdmin
        ? 'החיבור אומת מול OpenRouter והמפתח נשמר ב־Secret Manager. העוזר פעיל כבר עכשיו בניתוב אוטומטי.'
        : 'החיבור האישי אומת מול OpenRouter והמפתח נשמר מוצפן ב־Cloud KMS. העוזר פעיל כבר עכשיו בניתוב אוטומטי.');
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setSavingKey(false); }
  }

  // Proves the whole path — key, catalog, chosen model, a real reply — rather
  // than only that something was stored.
  async function runConnectionTest() {
    setTesting(true); setTestError(''); setTestResult(null); setError(''); setNotice('');
    try {
      const result = (await testAiConnection()).data;
      setTestResult(result);
      // Reflect the proven state immediately instead of waiting for a reload.
      setSettings((current) => current ? { ...current, lastVerifiedAtMs: result.verifiedAtMs, lastVerifiedModelId: result.model.id } : current);
      // The test just proved the key reaches OpenRouter, so fetch the catalog
      // it reported rather than leaving the picker empty.
      if (!models.length) await loadCatalog();
    } catch (cause) { setTestError(messageFrom(cause)); }
    finally { setTesting(false); }
  }

  async function disconnectKey() {
    const warning = isSuperAdmin
      ? 'למחוק את המפתח המערכתי? הוא יפסיק לעבוד מיד עבור כל המערכת, והגרסאות הקודמות יושמדו.'
      : 'למחוק את המפתח האישי שלך? הוא יימחק לצמיתות ותצטרך להזין מפתח חדש כדי להפעיל AI.';
    if (!window.confirm(warning)) return;
    setDisconnecting(true); setError(''); setNotice(''); setTestResult(null); setTestError('');
    try {
      const result = (await disconnectAi()).data;
      setSettings(result.configuration);
      setModels([]);
      setSelectedModelId('');
      setSelectionMode(result.configuration.selectionMode);
      setApiKey('');
      setReplacingKey(false);
      setNotice(isSuperAdmin
        ? `המפתח המערכתי נותק${result.destroyedVersions ? ` ו־${result.destroyedVersions} גרסאות קודמות הושמדו` : ''}. הזן מפתח חדש כדי לחבר מחדש.`
        : 'המפתח האישי נמחק. הזן מפתח חדש כדי לחבר מחדש.');
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setDisconnecting(false); }
  }

  async function saveModel() {
    if (selectionMode === 'fixed' && !selectedModelId) { setError('יש לבחור מודל קבוע לפני שמירת ההגדרה.'); return; }
    setSavingModel(true); setError(''); setNotice('');
    try {
      applyCatalog((await configureAi({ selectedModelId, selectionMode })).data);
      setNotice(selectionMode === 'fixed'
        ? (isSuperAdmin ? 'מודל המערכת הפעיל עודכן.' : 'המודל האישי הפעיל שלך עודכן.')
        : (isSuperAdmin ? 'הופעל ניתוב אוטומטי מערכתי מבוקר לפי משימה, עלות ויכולת.' : 'הופעל עבורך ניתוב אוטומטי מבוקר לפי משימה, עלות ויכולת.'));
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setSavingModel(false); }
  }

  // Providers with a count, so the dropdown says how much each one offers.
  const providers = useMemo(() => {
    const counts = new Map<string, number>();
    for (const model of models) counts.set(providerOf(model.id), (counts.get(providerOf(model.id)) ?? 0) + 1);
    return [...counts.entries()].map(([id, count]) => ({ id, count })).sort((a, b) => a.id.localeCompare(b.id));
  }, [models]);

  const filteredModels = useMemo(() => {
    const term = query.trim().toLowerCase();
    const totalPrice = (model: ModelInfo) => model.inputPricePerMillion + model.outputPricePerMillion;
    const matches = models.filter((model) => {
      // The search covers the display name and the id, and the id carries the
      // provider — so "anthropic" and "claude" both find the same models.
      if (term && !`${model.name} ${model.id}`.toLowerCase().includes(term)) return false;
      if (providerFilter && providerOf(model.id) !== providerFilter) return false;
      if (freeOnly && totalPrice(model) > 0) return false;
      if (toolsOnly && !model.supportsTools) return false;
      if (structuredOnly && !model.supportsStructuredOutput) return false;
      return true;
    });
    const byName = (a: ModelInfo, b: ModelInfo) => a.name.localeCompare(b.name);
    return matches.sort((a, b) => {
      if (sortBy === 'price-asc') return totalPrice(a) - totalPrice(b) || byName(a, b);
      if (sortBy === 'price-desc') return totalPrice(b) - totalPrice(a) || byName(a, b);
      if (sortBy === 'provider') return providerOf(a.id).localeCompare(providerOf(b.id)) || byName(a, b);
      if (sortBy === 'context') return b.contextLength - a.contextLength || byName(a, b);
      return byName(a, b);
    });
  }, [models, query, providerFilter, sortBy, freeOnly, toolsOnly, structuredOnly]);

  const selected = models.find((model) => model.id === selectedModelId);
  const connected = settings?.keyConfigured === true;
  const verified = connected && Boolean(settings?.lastVerifiedAtMs);
  return <section className="page ai-management-page">
    <div className="page-heading ai-page-heading"><div><span className="eyebrow">{isSuperAdmin ? 'סופר אדמין · בקרה מרכזית' : 'הגדרה אישית · פרטית'}</span><h1>{isSuperAdmin ? 'ניהול AI' : 'הגדרות AI אישיות'}</h1><p>{isSuperAdmin ? 'חיבור OpenRouter, בחירת מודל מערכת והחלטה אם לשמור על מודל קבוע או על ניתוב אוטומטי.' : 'חבר את חשבון OpenRouter שלך, בחר מודל אישי והפעל את העוזר באמצעות המפתח שלך בלבד.'}</p></div><button className="secondary-button" onClick={refreshCatalog} disabled={loading || loadingCatalog || !settings?.keyConfigured}><RefreshCw className={loading || loadingCatalog ? 'spin' : ''} size={16} /> רענון קטלוג</button></div>
    <div className="ai-status-strip"><span className={`connection-indicator ${settings?.keyConfigured ? (settings.lastVerifiedAtMs ? 'ready verified' : 'ready') : ''}`}>{settings?.keyConfigured ? <CheckCircle2 size={16} /> : <XCircle size={16} />}{!settings?.keyConfigured ? 'OpenRouter עדיין לא הוגדר' : settings.lastVerifiedAtMs ? (isSuperAdmin ? 'OpenRouter מערכתי מחובר ומאומת' : 'OpenRouter אישי מחובר ומאומת') : (isSuperAdmin ? 'OpenRouter מערכתי מחובר · טרם נבדק' : 'OpenRouter אישי מחובר · טרם נבדק')}</span><span><ShieldCheck size={15} /> המפתח לא נשמר ב־Firestore ולא מוצג מחדש לאחר השליחה</span><span><Cpu size={15} /> {loadingCatalog ? 'טוען קטלוג…' : models.length ? `${models.length} מודלים זמינים בקטלוג` : connected ? 'הקטלוג לא נטען' : 'טען קטלוג לאחר חיבור'}</span></div>
    {error && <p className="form-error ai-form-message">{error}</p>}{notice && <p className="success-message ai-form-message"><CheckCircle2 size={16} /> {notice}</p>}
    <div className="ai-management-grid">
      <section className={`ai-admin-card connection-card ${connected && !replacingKey ? 'is-connected' : ''}`}>
        <div className="ai-card-title"><span className="ai-card-icon"><KeyRound size={21} /></span><div><span className="eyebrow">01 · חיבור מאובטח</span><h2>מפתח OpenRouter {isSuperAdmin ? 'מערכתי' : 'אישי'}</h2></div></div>

        {connected && !replacingKey ? (
          /* Connected: this is a status panel, not a form. The field to type a
             key into only appears when the user asks to replace it. */
          <>
            <div className="connection-state">
              <div className="connection-state-head">
                <span className="connection-state-mark"><ShieldCheck size={19} /></span>
                <div>
                  <strong>{verified ? 'מחובר ומאומת' : 'מחובר'}</strong>
                  <span>{isSuperAdmin ? 'מפתח מערכתי · Secret Manager' : 'המפתח האישי שלך · מוצפן ב־Cloud KMS'}</span>
                </div>
              </div>
              <dl className="connection-facts">
                <div><dt>עודכן</dt><dd>{settings?.updatedAtMs ? relativeDay(settings.updatedAtMs) : '—'}</dd></div>
                <div><dt>אומת לאחרונה</dt><dd>{verified ? relativeDay(settings!.lastVerifiedAtMs!) : 'טרם נבדק'}</dd></div>
                <div><dt>קטלוג</dt><dd>{models.length ? `${models.length} מודלים` : 'טרם נטען'}</dd></div>
              </dl>
              {!verified && <p className="connection-hint">המפתח נשמר אך טרם הורץ מולו מבחן חיבור. הרץ בדיקה כדי לוודא שהוא באמת עובד.</p>}
            </div>

            <div className="connection-actions">
              <button className="secondary-button ai-action" onClick={runConnectionTest} disabled={testing}>{testing ? <RefreshCw className="spin" size={17} /> : <Activity size={17} />} בדיקת חיבור</button>
              <button className="secondary-button ai-action" onClick={() => { setReplacingKey(true); setTestResult(null); setTestError(''); setNotice(''); setError(''); }}><KeyRound size={17} /> החלפת מפתח</button>
              <button className="text-button danger" onClick={disconnectKey} disabled={disconnecting}>{disconnecting ? <RefreshCw className="spin" size={15} /> : <XCircle size={15} />} מחיקת המפתח</button>
            </div>
          </>
        ) : (
          <>
            <p>{isSuperAdmin ? 'הזן מפתח חדש כדי להגדיר או להחליף את החיבור המערכתי. המפתח נבדק מול OpenRouter, נכתב כגרסת Secret חדשה ונמחק מהטופס מיד לאחר הצלחה.' : 'הזן את המפתח האישי שלך. הוא נבדק מול OpenRouter, מוצפן ב־Cloud KMS ונשמר מחוץ ל־Firestore; הוא אינו מוצג מחדש.'}</p>
            <label className="ai-field"><span>OpenRouter API Key</span><input type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder="sk-or-v1-…" autoComplete="new-password" spellCheck="false" autoFocus={replacingKey} /></label>
            <div className="connection-actions">
              <button className="primary-button ai-action" onClick={saveKey} disabled={savingKey || !apiKey.trim()}>{savingKey ? <RefreshCw className="spin" size={17} /> : <ShieldCheck size={17} />}{connected ? 'בדיקה והחלפת מפתח' : 'בדיקה וחיבור מאובטח'}</button>
              {connected && <button className="text-button" onClick={() => { setReplacingKey(false); setApiKey(''); setError(''); }}>ביטול</button>}
            </div>
          </>
        )}

        {testResult && <div className="connection-test ok"><div className="connection-test-head"><CheckCircle2 size={17} /><strong>החיבור תקין</strong><span>{testResult.latencyMs} מ״ש</span></div><ul><li><small>מקור המפתח</small><strong>{testResult.keySource === 'cloud-kms' ? 'המפתח האישי שלך · Cloud KMS' : 'מפתח מערכת · Secret Manager'}</strong></li><li><small>קטלוג</small><strong>{testResult.modelCount} מודלים זמינים</strong></li><li><small>המודל שרץ בפועל</small><strong>{testResult.model.name}{testResult.routedAutomatically ? ' · ניתוב אוטומטי' : ' · מודל קבוע'}</strong></li><li><small>תשובת המודל</small><strong>{testResult.reply || '—'}</strong></li></ul>{testResult.fixedModelMissing && <p className="connection-test-warning">המודל הקבוע שבחרת אינו קיים יותר בקטלוג, ולכן ההפעלה נותבה אוטומטית. בחר מודל אחר בכרטיס הבא.</p>}</div>}
        {testError && <div className="connection-test failed"><div className="connection-test-head"><XCircle size={17} /><strong>החיבור נכשל</strong></div><p>{testError}</p></div>}
        <small className="security-note"><ShieldCheck size={14} /> {isSuperAdmin ? 'רק הסופר אדמין יכול לשלוח או להחליף מפתח מערכתי; הוא אינו מוצג מחדש.' : 'רק אתה יכול להשתמש במפתח האישי שלך; הוא אינו מוצג מחדש ואינו משותף עם משתמשים אחרים.'}</small>
      </section>
      <section className="ai-admin-card model-config-card">
        <div className="ai-card-title"><span className="ai-card-icon violet"><Cpu size={21} /></span><div><span className="eyebrow">02 · מדיניות הפעלה</span><h2>{isSuperAdmin ? 'מודל פעיל במערכת' : 'המודל האישי שלך'}</h2></div></div>

        {!connected ? (
          <p className="model-gate">חבר מפתח OpenRouter תקין כדי לטעון את הקטלוג ולבחור מודל.</p>
        ) : (
          <>
            <p>{isSuperAdmin ? 'הבחירה כאן חלה על העוזר, בדיקות התרגול וכל הפעלת AI במערכת של הסופר אדמין.' : 'הבחירה כאן חלה רק על העוזר והתרגולים בחשבון שלך.'}</p>
            <div className="mode-toggle">
              <button className={selectionMode === 'fixed' ? 'selected' : ''} onClick={() => setSelectionMode('fixed')}><strong>מודל קבוע</strong><small>כל הפעלה משתמשת במודל שתבחר</small></button>
              <button className={selectionMode === 'auto' ? 'selected' : ''} onClick={() => setSelectionMode('auto')}><strong>ניתוב אוטומטי</strong><small>התאמה לפי משימה, מחיר והקשר</small></button>
            </div>

            {selectionMode === 'fixed' && loadingCatalog && (
              <p className="model-gate loading"><RefreshCw className="spin" size={15} /> טוען את קטלוג המודלים מ־OpenRouter…</p>
            )}

            {selectionMode === 'fixed' && !loadingCatalog && catalogError && (
              <div className="catalog-error">
                <p><strong>לא ניתן לטעון את קטלוג המודלים.</strong> {catalogError}</p>
                <p className="catalog-error-note">החיבור עצמו תקין — זו תקלה בטעינת הרשימה בלבד.</p>
                <button className="secondary-button ai-action" onClick={loadCatalog}><RefreshCw size={16} /> נסה לטעון שוב</button>
              </div>
            )}

            {selectionMode === 'fixed' && !loadingCatalog && !catalogError && models.length === 0 && (
              <div className="catalog-error">
                <p>הקטלוג עדיין לא נטען.</p>
                <button className="secondary-button ai-action" onClick={loadCatalog}><RefreshCw size={16} /> טעינת הקטלוג</button>
              </div>
            )}

            {selectionMode === 'fixed' && !loadingCatalog && models.length > 0 && (
              <div className="model-browser">
                <div className="model-filters">
                  <label className="ai-field grow">
                    <span>חיפוש לפי שם או ספק</span>
                    <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="לדוגמה: claude, gemini, mistral…" />
                  </label>
                  <label className="ai-field">
                    <span>ספק</span>
                    <select value={providerFilter} onChange={(event) => setProviderFilter(event.target.value)}>
                      <option value="">כל הספקים ({providers.length})</option>
                      {providers.map((item) => <option key={item.id} value={item.id}>{item.id} ({item.count})</option>)}
                    </select>
                  </label>
                  <label className="ai-field">
                    <span>מיון</span>
                    <select value={sortBy} onChange={(event) => setSortBy(event.target.value as ModelSort)}>
                      <option value="price-asc">מחיר · מהזול ליקר</option>
                      <option value="price-desc">מחיר · מהיקר לזול</option>
                      <option value="name">שם · א׳ עד ת׳</option>
                      <option value="provider">ספק · א׳ עד ת׳</option>
                      <option value="context">חלון הקשר · מהגדול לקטן</option>
                    </select>
                  </label>
                </div>

                <div className="model-filter-chips">
                  <button className={freeOnly ? 'active' : ''} onClick={() => setFreeOnly((value) => !value)}>חינם בלבד</button>
                  <button className={toolsOnly ? 'active' : ''} onClick={() => setToolsOnly((value) => !value)}>תומך בכלים</button>
                  <button className={structuredOnly ? 'active' : ''} onClick={() => setStructuredOnly((value) => !value)}>פלט מובנה</button>
                  {(query || providerFilter || freeOnly || toolsOnly || structuredOnly) && (
                    <button className="clear" onClick={() => { setQuery(''); setProviderFilter(''); setFreeOnly(false); setToolsOnly(false); setStructuredOnly(false); }}>ניקוי סינון</button>
                  )}
                  <span className="model-count">{filteredModels.length} מתוך {models.length}</span>
                  <button className="model-reload" onClick={loadCatalog} disabled={loadingCatalog} title="רענון הקטלוג מ־OpenRouter"><RefreshCw className={loadingCatalog ? 'spin' : ''} size={13} /></button>
                </div>

                <div className="model-list" role="listbox" aria-label="קטלוג המודלים">
                  {filteredModels.length === 0
                    ? <p className="model-empty">אין מודל שתואם את הסינון. נסה מונח אחר או נקה את הסינון.</p>
                    : filteredModels.slice(0, 120).map((model) => (
                        <button
                          key={model.id}
                          role="option"
                          aria-selected={model.id === selectedModelId}
                          className={`model-option ${model.id === selectedModelId ? 'selected' : ''}`}
                          onClick={() => setSelectedModelId(model.id)}
                        >
                          <span className="model-option-main">
                            <strong>{model.name}</strong>
                            <small>{providerOf(model.id)}</small>
                          </span>
                          <span className="model-option-meta">
                            <span className={model.inputPricePerMillion === 0 && model.outputPricePerMillion === 0 ? 'price free' : 'price'}>
                              {model.inputPricePerMillion === 0 && model.outputPricePerMillion === 0 ? 'חינם' : `${formatPrice(model.inputPricePerMillion)} · ${formatPrice(model.outputPricePerMillion)}`}
                            </span>
                            <span className="context">{Math.round(model.contextLength / 1000)}K</span>
                            {model.supportsTools && <span className="tag" title="תומך בכלים">כלים</span>}
                            {model.supportsStructuredOutput && <span className="tag" title="פלט מובנה">מובנה</span>}
                          </span>
                        </button>
                      ))}
                  {filteredModels.length > 120 && <p className="model-empty">מוצגים 120 הראשונים מתוך {filteredModels.length}. צמצם את החיפוש כדי לראות את השאר.</p>}
                </div>
              </div>
            )}

            {selected && selectionMode === 'fixed' && <div className="selected-model-summary"><div><small>הבחירה הנוכחית</small><strong>{selected.name}</strong><span>{selected.id}</span></div><div className="model-facts"><span><small>קלט</small><strong>{formatPrice(selected.inputPricePerMillion)}</strong></span><span><small>פלט</small><strong>{formatPrice(selected.outputPricePerMillion)}</strong></span><span><small>הקשר</small><strong>{Math.round(selected.contextLength / 1000)}K</strong></span></div></div>}

            <button className="secondary-button ai-action" onClick={saveModel} disabled={savingModel || (selectionMode === 'fixed' && !selectedModelId)}>{savingModel ? <RefreshCw className="spin" size={17} /> : <Save size={17} />}{selectionMode === 'fixed' ? (isSuperAdmin ? 'שמירת מודל מערכת' : 'שמירת המודל האישי') : 'שמירת מדיניות ניתוב'}</button>
          </>
        )}
      </section>
    </div>
  </section>;
}

const STATUS_LABEL: Record<ManagedUser['status'], string> = {
  approved: 'פעיל',
  pending: 'ממתין לאישור',
  blocked: 'חסום',
};

function AdminPanel({ enabled }: { enabled: boolean }) {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [policy, setPolicy] = useState<AccessPolicy | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [busyUid, setBusyUid] = useState('');
  const [pendingDelete, setPendingDelete] = useState<ManagedUser | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const result = await listUsers();
      setUsers(result.data.users);
      setPolicy(result.data.policy);
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (enabled) void refresh(); }, [enabled, refresh]);

  async function changePolicy(openRegistration: boolean) {
    if (policy?.openRegistration === openRegistration) return;
    setSavingPolicy(true); setError(''); setNotice('');
    try {
      setPolicy((await setAccessPolicy(openRegistration)).data);
      setNotice(openRegistration
        ? 'כל חשבון Google מאומת מצטרף מעכשיו ומתחיל ללמוד מיד.'
        : 'מעכשיו כל חשבון חדש ימתין לאישורך. מי שכבר אושר ממשיך ללמוד כרגיל.');
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setSavingPolicy(false); }
  }

  async function update(user: ManagedUser, operation: 'approve' | 'block') {
    setBusyUid(user.uid); setError(''); setNotice('');
    try {
      await (operation === 'approve' ? approveUser(user.uid) : blockUser(user.uid));
      setNotice(operation === 'approve'
        ? `${user.email} מאושר ויכול ללמוד.`
        : `${user.email} נחסם. ההרשאות בוטלו והחיבורים הפעילים נותקו מיד.`);
      await refresh();
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setBusyUid(''); }
  }

  async function confirmDelete() {
    const user = pendingDelete;
    if (!user) return;
    setPendingDelete(null); setBusyUid(user.uid); setError(''); setNotice('');
    try {
      await deleteUserAccount(user.uid, 'מחיקה');
      setNotice(`${user.email} נמחק לצמיתות: החשבון, ההתקדמות, השיחות והמפתח האישי.`);
      await refresh();
    } catch (cause) { setError(messageFrom(cause)); }
    finally { setBusyUid(''); }
  }

  if (!enabled) return <section className="page"><div className="reader-empty error"><ShieldCheck /><h2>גישה מוגבלת</h2><p>ניהול גישה זמין לסופר אדמין בלבד.</p></div></section>;

  const waiting = users.filter((user) => user.status === 'pending').length;
  const open = policy?.openRegistration !== false;

  return <section className="page admin-page">
    <div className="page-heading">
      <div>
        <span className="eyebrow">סופר אדמין</span>
        <h1>ניהול גישה</h1>
        <p>מדיניות ההצטרפות, אישור, חסימה ומחיקה — כולם נאכפים בשרת מול הזהות המאומתת, לא בדפדפן.</p>
      </div>
      <button className="secondary-button" onClick={() => void refresh()} disabled={loading}><RefreshCw className={loading ? 'spin' : ''} size={16} /> רענון</button>
    </div>

    {/* The joining policy governs new accounts only. Turning it off never
        evicts anyone who is already learning, and never lifts a block. */}
    <section className="access-panel policy-panel">
      <div className="access-header">
        <div><KeyRound /><div><h2>מדיניות הצטרפות</h2><p>{open ? 'פתוח — כל חשבון Google מאומת מתחיל ללמוד מיד' : `סגור — כל חשבון חדש ממתין לאישורך${waiting ? ` · ${waiting} ממתינים` : ''}`}</p></div></div>
        <span className="admin-lock"><ShieldCheck size={15} /> {SUPER_ADMIN_EMAIL} בלבד</span>
      </div>
      <div className="mode-toggle policy-toggle">
        <button className={open ? 'selected' : ''} disabled={savingPolicy} onClick={() => void changePolicy(true)}>
          <strong>הצטרפות חופשית</strong>
          <small>כל חשבון Google מאומת נכנס ומתחיל ללמוד בלי אישור מוקדם.</small>
        </button>
        <button className={!open ? 'selected' : ''} disabled={savingPolicy} onClick={() => void changePolicy(false)}>
          <strong>אישור לכל משתמש חדש</strong>
          <small>חשבון חדש ממתין עד שתאשר אותו. מי שכבר אושר לא מושפע.</small>
        </button>
      </div>
      <small className="security-note"><ShieldCheck size={14} /> חסימה חזקה משתי המדיניות: חשבון חסום נשאר חסום עד שתאשר אותו מחדש, גם כשההצטרפות פתוחה.</small>
    </section>

    <section className="access-panel">
      <div className="access-header">
        <div><UsersRound /><div><h2>חשבונות</h2><p>{users.length ? `${users.length} חשבונות רשומים${waiting ? ` · ${waiting} ממתינים לאישור` : ''}` : 'אין עדיין חשבונות'}</p></div></div>
      </div>
      {error && <p className="form-error">{error}</p>}
      {notice && <p className="success-message">{notice}</p>}
      {users.length === 0 && !loading
        ? <div className="empty-access"><CheckCircle2 /><p>המערכת נקייה. חשבונות יופיעו כאן לאחר התחברות Google ראשונה.</p></div>
        : <div className="request-list">{users.map((user) => {
          const admin = user.role === 'super_admin';
          const busy = busyUid === user.uid;
          return <article className={`request-row status-${user.status}`} key={user.uid}>
            <span className="avatar request-avatar">{initials(user.displayName || user.email)}</span>
            <div className="request-person">
              <strong>{user.displayName || 'חשבון Google'}{admin && <span className="role-tag"><ShieldCheck size={11} /> סופר אדמין</span>}</strong>
              <span>{user.email}</span>
              <small>{STATUS_LABEL[user.status]} · פעילות אחרונה: {formatDate(user.lastSeenAt ?? undefined)}</small>
            </div>
            <div className="request-actions">
              {admin
                ? <span className="admin-immutable"><ShieldCheck size={14} /> מוגן — לא ניתן לחסימה או למחיקה</span>
                : <>
                  {user.status !== 'approved' && <button className="approve" disabled={busy} onClick={() => void update(user, 'approve')}><Check size={16} /> אישור</button>}
                  {user.status !== 'blocked' && <button className="block" disabled={busy} onClick={() => void update(user, 'block')}><XCircle size={16} /> חסימה</button>}
                  <button className="remove" disabled={busy} onClick={() => setPendingDelete(user)}><Flag size={15} /> מחיקה</button>
                </>}
            </div>
          </article>;
        })}</div>}
    </section>

    {pendingDelete && <ConfirmDialog
      eyebrow="פעולה בלתי הפיכה"
      title="למחוק את החשבון לצמיתות?"
      lead={`${pendingDelete.displayName || 'חשבון Google'} · ${pendingDelete.email}`}
      note="נמחקים החשבון עצמו, כל ההתקדמות והתוצרים, היסטוריית השיחות עם העוזר, הקבצים הפרטיים ומפתח ה־OpenRouter האישי. אין דרך לשחזר."
      confirmLabel="מחק לצמיתות"
      cancelLabel="ביטול"
      onConfirm={() => void confirmDelete()}
      onCancel={() => setPendingDelete(null)}
    />}
  </section>;
}

/* The system's own way of asking before something irreversible happens. Same
   paper, rules and type as the panel behind it, and the safe choice is the one
   that carries the weight. */
function ConfirmDialog({ eyebrow, title, lead, note, confirmLabel, cancelLabel, onConfirm, onCancel }: {
  eyebrow: string;
  title: string;
  lead: string;
  note: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelButton = useRef<HTMLButtonElement>(null);
  useEffect(() => { cancelButton.current?.focus(); }, []);
  useEffect(() => {
    function onKey(event: KeyboardEvent) { if (event.key === 'Escape') onCancel(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return <div className="system-overlay" onClick={onCancel}>
    <div className="system-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" onClick={(event) => event.stopPropagation()}>
      <header className="system-dialog-head">
        <span className="system-dialog-mark danger"><Flag size={19} /></span>
        <div><span className="eyebrow">{eyebrow}</span><h4 id="confirm-title">{title}</h4></div>
      </header>
      <p className="system-dialog-lead">{lead}</p>
      <p className="system-dialog-note">{note}</p>
      <footer className="system-dialog-actions">
        <button className="danger-button" onClick={onConfirm}>{confirmLabel}</button>
        <button className="primary-button" ref={cancelButton} onClick={onCancel}>{cancelLabel}</button>
      </footer>
    </div>
  </div>;
}

function weekTheme(week: number) { return ['indigo', 'sky', 'mint', 'violet', 'amber', 'rose', 'teal', 'slate'][week - 1]; }
function weekThemeLabel(week: number) { return ['SQL ונתונים', 'BigQuery', 'שכבה סמנטית', 'Text-to-SQL', 'Vertex וסוכנים', 'אבטחה', 'איכות והפעלה', 'ניהול והטמעה'][week - 1]; }
